import { isPossiblePhoneNumber } from 'react-phone-number-input';
import zod from 'zod';

export const MAX_COMPANY_LOGO_SIZE_MB = 5;
export const MAX_REG_DOC_SIZE_MB = 10;
export const COMPANY_LOGO_FILE_TYPES = '.jpg, .png';
export const REG_DOC_FILE_TYPES = '.pdf, .jpg, .png, .doc, .docx';

export const laylatAlomrFormSchema = (t: (key: string) => string) =>
  zod
    .object({
      supportType: zod.string(t('validation.form_errors.supporter_type_required')),
      companyName: zod
        .string(t('validation.form_errors.company_name_required'))
        .min(2, t('validation.form_errors.company_name_min'))
        .max(100, t('validation.form_errors.company_name_max')),
      city: zod
        .string(t('validation.form_errors.city_required'))
        .min(2, t('validation.form_errors.city_min'))
        .max(100, t('validation.form_errors.city_max')),
      contactPersonName: zod
        .string(t('validation.form_errors.contact_person_required'))
        .min(2, t('validation.form_errors.contact_person_min'))
        .max(100, t('validation.form_errors.contact_person_max'))
        .optional(),
      registrationNumber: zod
        .string()
        .refine(
          (val) => {
            // If empty, it's valid (will be checked later in superRefine for companies)
            if (!val || val.trim() === '') return true;
            // If provided, must be 10 digits
            return /^\d{10}$/.test(val);
          },
          { message: t('validation.form_errors.registration_number_invalid') }
        )
        .optional()
        .nullable(),
      phoneNumber: zod
        .string(t('validation.form_errors.phone_number_required'))
        .refine((val) => isPossiblePhoneNumber(val), {
          message: t('validation.form_errors.phone_number_invalid'),
        }),
      email: zod
        .string(t('validation.form_errors.email_required'))
        .email(t('validation.form_errors.email_invalid')),
      companyLogo: zod.any().optional(),
      registrationDocument: zod.any().optional(),
      letterOfAuthorization: zod.any().optional(),
      supportClassification: zod
        .array(zod.string())
        .min(1, t('validation.form_errors.support_classification_required')),
      supportCategory: zod
        .array(zod.string())
        .min(1, t('validation.form_errors.support_category_required')),
      expectedBeneficiaries: zod
        .number(t('validation.form_errors.expected_beneficiaries_required'))
        .min(1, t('validation.form_errors.expected_beneficiaries_required'))
        .max(1000, t('validation.form_errors.expected_beneficiaries_max')),
      supportDeliveryMechanism: zod
        .string(t('validation.form_errors.support_delivery_mechanism_required'))
        .min(1, t('validation.form_errors.support_delivery_mechanism_required')),
      estimatedSupportValue: zod
        .string(t('validation.form_errors.estimated_support_value_required'))
        .refine(
          (val) => {
            if (!val) return false;
            const num = parseFloat(val);
            return !isNaN(num) && num >= 100;
          },
          { message: t('validation.form_errors.support_value_min') }
        ),
      supportStartDate: zod
        .string(t('validation.form_errors.support_start_date_required'))
        .min(1, t('validation.form_errors.support_start_date_required'))
        .or(zod.date()),
      supportEndDate: zod
        .string(t('validation.form_errors.support_end_date_required'))
        .min(1, t('validation.form_errors.support_end_date_required'))
        .or(zod.date()),
      detailedSupportDescription: zod
        .string()
        .max(500, t('validation.form_errors.description_max'))
        .optional(),
      termsAccepted: zod
        .boolean(t('validation.form_errors.terms_required'))
        .refine((val) => val === true, {
          message: t('validation.form_errors.terms_required'),
        }),
    })
    .refine(
      (data) => {
        // Registration number is required only for companies
        if (data.supportType === 'Company') {
          if (!data.registrationNumber || data.registrationNumber.trim() === '') {
            return false;
          }
        }
        return true;
      },
      {
        message: t('validation.form_errors.registration_number_required'),
        path: ['registrationNumber'],
      }
    )
    .refine(
      (data) => {
        // Registration document is required only for companies
        if (data.supportType === 'Company') {
          if (!data.registrationDocument) {
            return false;
          }
        }
        return true;
      },
      {
        message: t('validation.form_errors.registration_document_required'),
        path: ['registrationDocument'],
      }
    )
    .refine(
      (data) => {
        if (!data.supportStartDate || !data.supportEndDate) return true;
        return new Date(data.supportEndDate) >= new Date(data.supportStartDate);
      },
      {
        message: t('validation.form_errors.date_range_invalid'),
        path: ['supportEndDate'],
      }
    )
    .refine(
      (data) => {
        if (data.supportType !== 'Individual') {
          if (!data.contactPersonName) {
            return false;
          }
        }
        return true;
      },
      {
        message: t('validation.form_errors.contact_person_required'),
        path: ['contactPersonName'],
      }
    );

export type LaylatAlomrFormSchemaProps = zod.infer<ReturnType<typeof laylatAlomrFormSchema>>;

export const supportClassifications = [
  {
    value: 'Financial support for beneficiaries',
    label: 'financial_support_for_beneficiaries',
  },
  {
    value: 'Cars',
    label: 'cars',
  },
  {
    value: 'Furniture and household appliances',
    label: 'furniture_and_household_appliances',
  },
  {
    value: 'Honeymoon packages',
    label: 'honeymoon_packages',
  },
  {
    value: 'Hotel services',
    label: 'hotel_services',
  },
  {
    value: 'Technical services',
    label: 'technical_services',
  },
  {
    value: 'Transportation',
    label: 'transportation',
  },
  {
    value: 'Training and development courses',
    label: 'training_and_development_courses',
  },
  {
    value: 'Medical and health services',
    label: 'medical_and_health_services',
  },
  {
    value: 'Beauty services',
    label: 'beauty_services',
  },
  {
    value: "Women's clothing",
    label: 'womens_clothing',
  },
  {
    value: "Men's clothing",
    label: 'mens_clothing',
  },
  {
    value: 'Perfumes and incense',
    label: 'perfumes_and_incense',
  },
  {
    value: 'Flowers and gifts',
    label: 'flowers_and_gifts',
  },
  {
    value: 'Event planning and management',
    label: 'event_planning_and_management',
  },
  {
    value: 'Event hospitality',
    label: 'event_hospitality',
  },
  {
    value: 'Other support (please specify)',
    label: 'other_support',
  },
];
